import gql from 'graphql-tag';
import React from 'react';

import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import APIUtil from 'util/APIUtil';

import {GetEntryNameQuery, GetEntryNameQueryVariables} from './__generated/GetEntryNameQuery';
import {GetWorksheetNameQuery, GetWorksheetNameQueryVariables} from './__generated/GetWorksheetNameQuery';
import {getDocumentIdFromStep} from './OutputsUtil';

export const GET_WORKSHEET_NAME_QUERY = gql`
  query GetWorksheetNameQuery($id: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    worksheet(id: $id) {
      ... on WorksheetNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const GET_ENTRY_NAME_QUERY = gql`
  query GetEntryNameQuery($id: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    entry(id: $id) {
      ... on EntryNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface DocumentNameProps {
  step: GetAnalysisPipelineStep;
}

export default function DocumentName({step}: DocumentNameProps): JSX.Element {
  const documentId = getDocumentIdFromStep(step);
  if (APIUtil.apiIdToType(documentId) === ModelTypeConstants.WORKSHEET) {
    return <WorksheetName documentId={documentId} />;
  } else {
    return <EntryName documentId={documentId} />;
  }
}

interface WorksheetAndEntryNameProps {
  documentId: string;
}

function WorksheetName({documentId}: WorksheetAndEntryNameProps): JSX.Element {
  return (
    <BenchlingQuery<GetWorksheetNameQuery, GetWorksheetNameQueryVariables>
      query={GET_WORKSHEET_NAME_QUERY}
      renderLoading={() => 'Loading...'}
      variables={{
        id: documentId,
      }}
    >
      {(data) => (isInaccessible(data.worksheet) ? 'Inaccessible' : data.worksheet.name)}
    </BenchlingQuery>
  );
}

function EntryName({documentId}: WorksheetAndEntryNameProps): JSX.Element {
  return (
    <BenchlingQuery<GetEntryNameQuery, GetEntryNameQueryVariables>
      query={GET_ENTRY_NAME_QUERY}
      renderLoading={() => 'Loading...'}
      variables={{
        id: documentId,
      }}
    >
      {(data) => (isInaccessible(data.entry) ? 'Inaccessible' : data.entry.name)}
    </BenchlingQuery>
  );
}
